import { FormModal } from '@/components/form-modal.js';
import { FormCheckbox } from '@/components/forms/form-checkbox.js';
import { Button } from '@/components/ui/button.js';
import { DialogDescription } from '@/components/ui/dialog.js';
import { useToast } from '@/components/ui/hooks/use-toast.js';
import { deactivateOrg, getOrg, invalidateOrgQueries } from '@/lib/api-calls.js';
import { airServerErrorFrom } from '@/lib/error-handling.js';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';

const formSchema = z.object({
  confirmed: z.boolean().default(false),
});

export function DeactivateOrg({ orgId, districtName }: { orgId: string; districtName: string }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: deactivateOrg,
  });

  const onSubmit = async ({
    formData,
    closeModal,
  }: {
    formData: z.infer<typeof formSchema>;
    closeModal: () => void;
  }) => {
    if (!formData.confirmed) {
      toast({
        title: 'Please confirm that you want to deactivate this district before continuing.',
      });
      return;
    }

    try {
      await mutateAsync(orgId);
      closeModal();
      toast({ title: `${districtName} is now inactive` });
      queryClient.invalidateQueries(getOrg(orgId));
      invalidateOrgQueries(queryClient);
    } catch (e) {
      const error = airServerErrorFrom(e);
      toast({
        variant: 'destructive',
        title: 'Failed to deactivate district',
        description: error?.userFacingMessage,
      });
    }
  };

  return (
    <FormModal
      modalTitle="Deactivate district"
      dataTestId="district-modal-deactivate"
      trigger={
        <Button type="button" variant="outline" data-testid="district-button-deactivate">
          Deactivate
        </Button>
      }
      onSubmit={onSubmit}
      dialogContentProps={{ className: 'max-w-md' }}
      submitButtonProps={{
        children: 'Deactivate',
        loading: isPending,
      }}
      formSchema={formSchema}
      confirmBtnLabel="Deactivate"
      defaultValues={{ confirmed: false }}
    >
      {({ form }) => (
        <div className="my-6 space-y-4">
          <DialogDescription>
            {districtName} will stay in historical records with status inactive. It will no longer appear on the default
            district list.
          </DialogDescription>
          <FormCheckbox
            name="confirmed"
            label="I understand this district will be marked inactive"
            form={form}
            horizontal
          />
        </div>
      )}
    </FormModal>
  );
}
